 
import type {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  RowData,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table';
import React, { useState } from 'react';

import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';

import DefualtColumn from './EditColFn';

type Props = {
  items: IOneItemInPrepareForRequest[];
  setItems: React.Dispatch<React.SetStateAction<IOneItemInPrepareForRequest[]>>;
  columns: ColumnDef<IOneItemInPrepareForRequest>[];
  initialPageSize?: number;
  initialPageIndex?: number;
};

declare module '@tanstack/table-core' {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void;
    skipAutoResetPageIndex: () => void;
  }

  // Define built-in filter functions that come with TanStack Table
  interface FilterFns {
    // Built-in filters that come with TanStack
    includesString: FilterFn<unknown>;
    includesStringSensitive: FilterFn<unknown>;
    equalsString: FilterFn<unknown>;
    equalsStringSensitive: FilterFn<unknown>;
    arrIncludes: FilterFn<unknown>;
    arrIncludesAll: FilterFn<unknown>;
    arrIncludesSome: FilterFn<unknown>;
    equals: FilterFn<unknown>;
    weakEquals: FilterFn<unknown>;
    inNumberRange: FilterFn<unknown>;
    // Custom filters
    BooleanFilter: FilterFn<unknown>;
    CustomFilter: FilterFn<unknown>;
    DiscountStatusFilter: FilterFn<unknown>;
    WholeSaleStatusFilter: FilterFn<unknown>;
    RetailStatusFilter: FilterFn<unknown>;
  }
}

// Define filter functions outside the component
const booleanFilterFn: FilterFn<IOneItemInPrepareForRequest> = (
  row,
  columnId,
  filterValue,
) => {
  const value = row.getValue(columnId);
  if (filterValue === 'true') {
    return !!value;
  }
  if (filterValue === 'false') {
    return !value;
  }
  return false;
};

const customFilterFn: FilterFn<IOneItemInPrepareForRequest> = (
  row,
  _columnId,
  filterValue,
) => {
  if (filterValue === 'unlinked') {
    return !row.original?.HealthyItemID;
  } else if (filterValue === 'linked') {
    return !!row.original?.HealthyItemID;
  } else if (filterValue === 'diff') {
    return Boolean(
      row.original?.HealthyItemID &&
        Math.abs(
          (row.original?.Price || 0) - (row.original?.healthyItemPrice || 0),
        ) > 0.5,
    );
  } else if (filterValue === 'invoice') {
    return row.original?.Qty > 0;
  }
  return false;
};

const discountStatusFilterFn: FilterFn<IOneItemInPrepareForRequest> = (
  row,
  columnId,
  filterValue,
) => {
  if (!filterValue) return true;
  const value = row.getValue(columnId) as string | undefined;
  return value === filterValue;
};

const wholeSaleStatusFilterFn: FilterFn<IOneItemInPrepareForRequest> = (
  row,
  columnId,
  filterValue,
) => {
  if (!filterValue) return true;
  const value = row.getValue(columnId) as string | undefined;
  return value === filterValue;
};

const retailStatusFilterFn: FilterFn<IOneItemInPrepareForRequest> = (
  row,
  columnId,
  filterValue,
) => {
  if (!filterValue) return true;
  const value = row.getValue(columnId) as string | undefined;
  return value === filterValue;
};

const fuzzyFilterFn: FilterFn<IOneItemInPrepareForRequest> = (
  row,
  columnId,
  filterValue,
) => {
  const value = row.getValue(columnId);
  return String(value)
    .toLowerCase()
    .includes(String(filterValue).toLowerCase());
};

function useSkipper() {
  const shouldSkipRef = React.useRef(true);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = React.useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  React.useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip] as const;
}

export default function useTable({
  items,
  setItems,
  columns,
  initialPageSize = 10,
  initialPageIndex = 0,
}: Props) {
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });

  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: DefualtColumn,
    autoResetPageIndex,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    getPaginationRowModel: getPaginationRowModel(), // Add pagination support
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    initialState: {
      columnVisibility: {
        RetailStatus: false,
        WholeSaleSalesStatus: false,
        isLastUpdate: false,
        DiscountStatus: false,
      },
      pagination: {
        pageIndex: initialPageIndex,
        pageSize: initialPageSize,
      },
    },

    // Provide our updateData function to our table meta
    meta: {
      updateData: (rowIndex, columnId, value) => {
        // Skip page index reset until after next rerender
        skipAutoResetPageIndex();
        setItems((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex]!,
                [columnId]: value,
              };
            }
            return row;
          }),
        );
      },
      skipAutoResetPageIndex,
    },
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    debugTable: false,
    filterFns: {
      // Provide all required built-in filters
      includesString: fuzzyFilterFn,
      includesStringSensitive: fuzzyFilterFn,
      equalsString: fuzzyFilterFn,
      equalsStringSensitive: fuzzyFilterFn,
      arrIncludes: fuzzyFilterFn,
      arrIncludesAll: fuzzyFilterFn,
      arrIncludesSome: fuzzyFilterFn,
      equals: booleanFilterFn,
      weakEquals: booleanFilterFn,
      inNumberRange: customFilterFn,
      // Custom filters
      BooleanFilter: booleanFilterFn,
      CustomFilter: customFilterFn,
      DiscountStatusFilter: discountStatusFilterFn,
      WholeSaleStatusFilter: wholeSaleStatusFilterFn,
      RetailStatusFilter: retailStatusFilterFn,
    },
  });

  // Configure column filter functions after table is created
  React.useEffect(() => {
    // Set filter functions for columns that need them
    table.getAllColumns().forEach((column) => {
      const columnId = column.id;
      if (columnId === 'isActive' || columnId === 'isLastUpdate') {
        // For boolean columns
        column.columnDef.filterFn = booleanFilterFn;
      } else if (columnId === 'DiscountStatus') {
        // For discount status filtering
        column.columnDef.filterFn = discountStatusFilterFn;
      } else if (columnId === 'WholeSaleSalesStatus') {
        // For wholesale status filtering
        column.columnDef.filterFn = wholeSaleStatusFilterFn;
      } else if (columnId === 'RetailStatus') {
        // For retail status filtering
        column.columnDef.filterFn = retailStatusFilterFn;
      } else if (columnId === 'Price') {
        // For linking status
        column.columnDef.filterFn = customFilterFn;
      } else {
        // Default fuzzy filter for text columns
        column.columnDef.filterFn = fuzzyFilterFn;
      }
    });
  }, [table]);

  return table;
}
